import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function NewNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();

  // =========================
  // SCROLL
  // =========================

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // =========================
  // RESPONSIVE MENU
  // =========================

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // =========================
  // PREVENT BODY SCROLL
  // =========================

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // =========================
  // SCROLL TO SECTION
  // =========================

  const scrollToSection = (sectionId) => {
    closeMenu();

    const section = document.getElementById(sectionId);

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // =========================
  // LOGIN
  // =========================

  const handleLogin = () => {
    closeMenu();
    navigate("/LoginPage");
  };

  return (
    <>
      {/* ================================================= */}
      {/* NAVBAR */}
      {/* ================================================= */}

      <header
        className={`
          fixed
          inset-x-0
          top-0
          z-[9999]
          transition-all
          duration-500
          ${
            scrolled
              ? "border-b border-white/10 bg-[#020617]/80 backdrop-blur-2xl"
              : "border-b border-transparent bg-[#020617]/20 backdrop-blur-xl"
          }
        `}
      >
        <div
          className="
            section-container
            flex
            h-[76px]
            items-center
            justify-between
          "
        >
          {/* ================= LOGO ================= */}

          <button
            onClick={() => scrollToSection("home")}
            className="shrink-0"
          >
            <img
              src="https://claimpro-dev.s3.ap-south-1.amazonaws.com/Assets/navbar/logo-navbar-compressed.png"
              alt="ClaimPro Assist"
              className="
                h-11
                w-auto
                object-contain
                md:h-12
              "
            />
          </button>

          {/* ================= DESKTOP NAV ================= */}

          <nav
            className="
              hidden
              items-center
              gap-10
              text-[11px]
              font-medium
              uppercase
              tracking-[0.30em]
              text-white/70
              lg:flex
            "
          >
            {/* Services */}

            <button
              onClick={() => scrollToSection("services")}
              className="
                relative
                transition
                hover:text-white
                after:absolute
                after:-bottom-2
                after:left-0
                after:h-[2px]
                after:w-0
                after:bg-[#21A6E9]
                after:transition-all
                hover:after:w-full
              "
            >
              Services
            </button>

            {/* About */}

            <button
              onClick={() => scrollToSection("about")}
              className="
                relative
                transition
                hover:text-white
                after:absolute
                after:-bottom-2
                after:left-0
                after:h-[2px]
                after:w-0
                after:bg-[#21A6E9]
                after:transition-all
                hover:after:w-full
              "
            >
              About
            </button>

            {/* Network */}

            <button
              onClick={() => scrollToSection("network")}
              className="
                relative
                transition
                hover:text-white
                after:absolute
                after:-bottom-2
                after:left-0
                after:h-[2px]
                after:w-0
                after:bg-[#21A6E9]
                after:transition-all
                hover:after:w-full
              "
            >
              Network
            </button>

            {/* Team */}

            <button
              onClick={() => scrollToSection("team")}
              className="
                relative
                transition
                hover:text-white
                after:absolute
                after:-bottom-2
                after:left-0
                after:h-[2px]
                after:w-0
                after:bg-[#21A6E9]
                after:transition-all
                hover:after:w-full
              "
            >
              Team
            </button>

            {/* Contact */}

            <button
              onClick={() => scrollToSection("contact")}
              className="
                relative
                transition
                hover:text-white
                after:absolute
                after:-bottom-2
                after:left-0
                after:h-[2px]
                after:w-0
                after:bg-[#21A6E9]
                after:transition-all
                hover:after:w-full
              "
            >
              Contact
            </button>
          </nav>

          {/* ================= LOGIN ================= */}

          <div className="hidden lg:block">
            <button
              onClick={handleLogin}
              className="
                rounded-full
                border
                border-white/20
                bg-white/5
                px-6
                py-2.5
                text-xs
                font-semibold
                tracking-wide
                text-white
                backdrop-blur-xl
                transition-all
                duration-300
                hover:border-[#21A6E9]
                hover:bg-[#21A6E9]
                hover:text-[#020617]
              "
            >
              Login
            </button>
          </div>

          {/* ================= MOBILE MENU BUTTON ================= */}

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="
              text-white
              transition-transform
              duration-300
              lg:hidden
            "
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* ================================================= */}
      {/* MOBILE MENU */}
      {/* ================================================= */}

      <div
        className={`
          fixed
          inset-0
          z-[9998]
          transition-all
          duration-300
          lg:hidden
          ${
            menuOpen
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0"
          }
        `}
      >
        {/* Background */}

        <div
          onClick={closeMenu}
          className="
            absolute
            inset-0
            bg-[#020617]/95
            backdrop-blur-2xl
          "
        />

        {/* Drawer */}

        <div
          className={`
            absolute
            left-0
            top-[76px]
            h-[calc(100vh-76px)]
            w-full
            bg-[#020617]
            transition-transform
            duration-300
            ease-in-out
            ${
              menuOpen
                ? "translate-y-0"
                : "-translate-y-full"
            }
          `}
        >
          <nav className="flex h-full flex-col px-8 py-6">

            {/* Services */}

            <button
              onClick={() => scrollToSection("services")}
              className="
                border-b
                border-white/10
                py-6
                text-left
                text-sm
                font-semibold
                uppercase
                tracking-[0.28em]
                text-white
                transition
                hover:text-[#21A6E9]
              "
            >
              Services
            </button>

            {/* About */}

            <button
              onClick={() => scrollToSection("about")}
              className="
                border-b
                border-white/10
                py-6
                text-left
                text-sm
                font-semibold
                uppercase
                tracking-[0.28em]
                text-white
                transition
                hover:text-[#21A6E9]
              "
            >
              About
            </button>

            {/* Network */}

            <button
              onClick={() => scrollToSection("network")}
              className="
                border-b
                border-white/10
                py-6
                text-left
                text-sm
                font-semibold
                uppercase
                tracking-[0.28em]
                text-white
                transition
                hover:text-[#21A6E9]
              "
            >
              Network
            </button>

            {/* Team */}

            <button
              onClick={() => scrollToSection("team")}
              className="
                border-b
                border-white/10
                py-6
                text-left
                text-sm
                font-semibold
                uppercase
                tracking-[0.28em]
                text-white
                transition
                hover:text-[#21A6E9]
              "
            >
              Team
            </button>

            {/* Contact */}

            <button
              onClick={() => scrollToSection("contact")}
              className="
                py-6
                text-left
                text-sm
                font-semibold
                uppercase
                tracking-[0.28em]
                text-white
                transition
                hover:text-[#21A6E9]
              "
            >
              Contact
            </button>

            {/* Bottom Login */}

            <div className="mt-auto pb-8">
              <button
                onClick={handleLogin}
                className="
                  w-full
                  rounded-full
                  bg-[#21A6E9]
                  py-4
                  text-base
                  font-semibold
                  text-[#020617]
                  transition
                  duration-300
                  hover:scale-[1.02]
                "
              >
                Login
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}